import React from "react";
import { Button, Card, Collapse } from "react-bootstrap";
import { FiChevronDown } from "react-icons/fi";
import { Phase } from "./ProjectConfigurationTypes.ts";
import PartComponent from "./PartComponent.tsx";

interface PhaseComponentProps {
  phase: Phase;
  phaseIndex: number;
  phaseId: string;
  isExpanded: boolean;
  projectId: string;
  subjects: any[];
  topicsCache: Record<string, any[]>;
  contentStatus?: {
    video?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    notes?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    mcq?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    project_coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null;
  dragHandleProps: any;
  onToggleExpand: () => void;
  onPartToggleExpand: (partId: string) => void;
  onTaskToggleExpand: (taskId: string) => void;
  onPreviewContent: (partIndex: number, taskIndex: number, subtaskIndex: number, type: "video" | "notes", content: any) => void;
  onFetchTopics: (subjectId: string) => Promise<void>;
  isPartExpanded: (partId: string) => boolean;
  isTaskExpanded: (taskId: string) => boolean;
  isSubtaskExpanded: (subtaskId: string) => boolean;
  toggleSubtaskExpand: (subtaskId: string) => void;
}

/**
 * PhaseComponent: Renders a phase card with drag-and-drop support for parts
 * This component displays a single phase and manages its parts.
 * It provides functionality for:
 * - Expanding/collapsing the phase
 * - Editing the phase name
 * - Adding new parts
 * - Deleting the phase
 * - Drag-and-drop reordering of parts
 * - Rendering PartComponent children for each part
 */
const PhaseComponent: React.FC<PhaseComponentProps> = ({
  phase,
  phaseIndex,
  phaseId,
  isExpanded,
  projectId,
  subjects,
  topicsCache,
  contentStatus,
  dragHandleProps,
  onToggleExpand,
  onPartToggleExpand,
  onTaskToggleExpand,
  onPreviewContent,
  onFetchTopics,
  isPartExpanded,
  isTaskExpanded,
  isSubtaskExpanded,
  toggleSubtaskExpand,
}) => {
  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">

          <Button
            variant="light"
            size="sm"
            className="p-1"
            onClick={onToggleExpand}
          >
            <FiChevronDown
              size={18}
              style={{
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)"
              }}
            />
          </Button>
          <span className="fw-bold me-2">{phaseId}</span>
          <span>{phase.name || "—"}</span>
        </div>
        
      </Card.Header>
      <Collapse in={isExpanded}>
        <Card.Body>
          {phase.parts.map((part, partIndex) => {
            const partId = `${phaseId}-${part.id}`;
            return (
              <div key={part.id}>
                <PartComponent
                  part={part}
                  phaseIndex={phaseIndex}
                  partIndex={partIndex}
                  partId={partId}
                  isExpanded={isPartExpanded(partId)}
                  projectId={projectId}
                  subjects={subjects}
                  topicsCache={topicsCache}
                  contentStatus={contentStatus}
                  dragHandleProps={undefined}
                  onToggleExpand={() => onPartToggleExpand(partId)}
                  onTaskToggleExpand={onTaskToggleExpand}
                  onPreviewContent={(taskIndex, subtaskIndex, type, content) => onPreviewContent(partIndex, taskIndex, subtaskIndex, type, content)}
                  onFetchTopics={onFetchTopics}
                  isTaskExpanded={isTaskExpanded}
                  isSubtaskExpanded={isSubtaskExpanded}
                  toggleSubtaskExpand={toggleSubtaskExpand}
                />
              </div>
            );
          })}
        </Card.Body>
      </Collapse>
    </Card>
  );
};

export default PhaseComponent;

